import React, { useState } from 'react';
import { Coupon } from '../types/Coupon';
import { MdDelete, MdEdit, MdSearch, MdFilterList } from 'react-icons/md';
import { CouponInput } from './CouponInput';

interface CouponListProps {
  coupons: Coupon[];
  onSelectCoupon?: (coupon: Coupon) => void;
  onDeleteCoupon: (id: string) => void;
  onAddCoupon?: () => void;
}

export const CouponList: React.FC<CouponListProps> = ({ 
  coupons, 
  onSelectCoupon, 
  onDeleteCoupon,
  onAddCoupon
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'name' | 'date' | 'usage'>('date');

  const getStatusLabel = (status: string) => {
    switch(status) {
      case 'active': return '有効';
      case 'inactive': return '無効';
      case 'expired': return '期限切れ';
      case 'draft': return '下書き';
      default: return status;
    }
  };

  const getStatusClass = (status: string) => {
    switch(status) {
      case 'active': return 'status-active';
      case 'inactive': return 'status-inactive';
      case 'expired': return 'status-expired';
      case 'draft': return 'status-draft';
      default: return '';
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const formatDiscount = (coupon: Coupon) => {
    if (coupon.discountType === 'percentage') {
      return `${coupon.discountValue}%割引`;
    } else {
      return `¥${coupon.discountValue.toLocaleString()}割引`;
    }
  };

  // フィルタリングとソート
  const filteredCoupons = coupons
    .filter(coupon => {
      const matchesSearch = 
        coupon.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        coupon.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        coupon.description.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || coupon.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      switch(sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'date':
          return b.createdAt.getTime() - a.createdAt.getTime();
        case 'usage':
          return b.usedCount - a.usedCount;
        default:
          return 0;
      }
    });

  return (
    <div className="project-list-container">
      <div className="project-list-header">
        <div className="project-list-title">
          <h2>クーポン一覧</h2>
          <span className="project-count">{filteredCoupons.length}件のクーポン</span>
        </div>
        {onAddCoupon && (
          <button className="btn-add-coupon" onClick={onAddCoupon}>
            ＋ 新規追加
          </button>
        )}
      </div>

      {/* 検索・フィルター */}
      <div className="project-list-filters">
        <div className="search-box">
          <span className="search-icon">{React.createElement(MdSearch as any)}</span>
          <input
            type="text"
            placeholder="クーポン名、コード、説明で検索..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <div className="filter-item">
            <span className="filter-icon">{React.createElement(MdFilterList as any)}</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">すべてのステータス</option>
              <option value="active">有効</option>
              <option value="inactive">無効</option>
              <option value="expired">期限切れ</option>
              <option value="draft">下書き</option>
            </select>
          </div>

          <div className="filter-item">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'name' | 'date' | 'usage')}
              className="filter-select"
            >
              <option value="date">作成日順</option>
              <option value="name">名前順</option>
              <option value="usage">使用回数順</option>
            </select>
          </div>
        </div>
      </div>

      {/* クーポンリスト */}
      <div className="project-list-content">
        {filteredCoupons.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <h3>クーポンが見つかりません</h3>
            <p>検索条件を変更するか、新しいクーポンを作成してください</p>
          </div>
        ) : (
          <div className="project-grid">
            {filteredCoupons.map((coupon) => (
              <div
                key={coupon.id}
                className="project-card"
                onClick={() => onSelectCoupon && onSelectCoupon(coupon)}
              >
                <div className="project-card-header">
                  <div className="project-card-title-section">
                    <h3 className="project-card-title">{coupon.name}</h3>
                    <div className="project-card-code">{coupon.code}</div>
                  </div>
                  <div className="project-card-actions" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="icon-button icon-button-danger"
                      onClick={() => onDeleteCoupon(coupon.id)}
                      title="削除"
                    >
                      {React.createElement(MdDelete as any)}
                    </button>
                  </div>
                </div>

                <div className="project-card-body">
                  {coupon.description && (
                    <p className="project-card-description">{coupon.description}</p>
                  )}

                  <div className="project-card-info">
                    <div className="info-row">
                      <span className="info-label">割引内容:</span>
                      <span className="info-value">{formatDiscount(coupon)}</span>
                    </div>

                    <div className="info-row">
                      <span className="info-label">有効期間:</span>
                      <span className="info-value">
                        {formatDate(coupon.startDate)} 〜 {formatDate(coupon.endDate)}
                      </span>
                    </div>

                    {coupon.minPurchaseAmount && (
                      <div className="info-row">
                        <span className="info-label">最低購入金額:</span>
                        <span className="info-value">¥{coupon.minPurchaseAmount.toLocaleString()}</span>
                      </div>
                    )}

                    <div className="info-row">
                      <span className="info-label">使用状況:</span>
                      <span className="info-value">
                        {coupon.usedCount}回
                        {coupon.usageLimit && ` / ${coupon.usageLimit}回`}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="project-card-footer">
                  <div className="project-card-meta">
                    <span className={`status-badge ${getStatusClass(coupon.status)}`}>
                      {getStatusLabel(coupon.status)}
                    </span>
                    <span className="project-card-date">
                      作成日: {formatDate(coupon.createdAt)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

